import { supabase } from '../config/supabase';
import { FinancialExpense, FinancialExpenseStatus } from '../types/financial-expense';

export async function readFinancialExpenses(clientId: string, month: string): Promise<FinancialExpense[]> {
  const { from, until } = monthBounds(month);
  const { data, error } = await supabase.rpc('financial_a14_read_expenses', {
    p_client_id: clientId,
    p_competence_from: from,
    p_competence_until: until,
  });
  if (error) throw normalizeExpenseError(error);
  if (!Array.isArray(data)) throw new Error('A consulta de despesas retornou um formato inválido.');
  return data as FinancialExpense[];
}

export async function saveFinancialExpense(input: {
  clientId: string;
  expenseId?: string | null;
  category: string;
  status: Exclude<FinancialExpenseStatus, 'paid'>;
  description: string;
  amount: number;
  competenceDate: string;
  dueDate?: string | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc('financial_a7_save_expense', {
    p_client_id: input.clientId,
    p_expense_id: input.expenseId ?? null,
    p_category: input.category,
    p_status: input.status,
    p_description: input.description,
    p_amount: input.amount,
    p_competence_date: input.competenceDate,
    p_due_date: input.dueDate || null,
  });
  if (error) throw normalizeExpenseError(error);
  if (typeof data !== 'string') throw new Error('O salvamento da despesa retornou um identificador inválido.');
  return data;
}

export async function payFinancialExpense(input: {
  clientId: string;
  expenseId: string;
  paidAt: string;
  paymentMethod: string;
  cashAccountId: string;
  idempotencyKey: string;
  notes?: string | null;
}): Promise<{ expenseId: string; expensePaymentId: string; cashMovementId: string; amount: number }> {
  const { data, error } = await supabase.rpc('financial_a14_pay_expense', {
    p_client_id: input.clientId,
    p_expense_id: input.expenseId,
    p_paid_at: input.paidAt,
    p_payment_method: input.paymentMethod,
    p_cash_account_id: input.cashAccountId,
    p_idempotency_key: input.idempotencyKey,
    p_notes: input.notes?.trim() || null,
  });
  if (error) throw normalizeExpenseError(error);
  if (!data || typeof data.expenseId !== 'string' || typeof data.expensePaymentId !== 'string') {
    throw new Error('O pagamento da despesa retornou um formato inválido.');
  }
  return data as { expenseId: string; expensePaymentId: string; cashMovementId: string; amount: number };
}

export async function reverseFinancialExpensePayment(input: {
  clientId: string;
  expensePaymentId: string;
  reversedAt: string;
  reason: string;
}): Promise<{ expenseId: string; reversalPaymentId: string; cashMovementId: string }> {
  const { data, error } = await supabase.rpc('financial_a14_reverse_expense_payment', {
    p_client_id: input.clientId,
    p_expense_payment_id: input.expensePaymentId,
    p_reversed_at: input.reversedAt,
    p_reason: input.reason.trim(),
  });
  if (error) throw normalizeExpenseError(error);
  if (!data || typeof data.reversalPaymentId !== 'string') throw new Error('O estorno retornou um formato inválido.');
  return data as { expenseId: string; reversalPaymentId: string; cashMovementId: string };
}

export async function copyFinancialExpenses(input: {
  clientId: string;
  sourceMonth: string;
  targetMonth: string;
}): Promise<{ sourceMonth: string; targetMonth: string; copiedCount: number; skippedCount: number; paymentsCopied: number }> {
  if (input.sourceMonth === input.targetMonth) {
    throw new Error('O mês de origem deve ser diferente do mês de destino.');
  }

  const [sourceExpenses, targetExpenses] = await Promise.all([
    readFinancialExpenses(input.clientId, input.sourceMonth),
    readFinancialExpenses(input.clientId, input.targetMonth),
  ]);
  const targetKeys = new Set(targetExpenses.map(expenseCopyKey));
  let copiedCount = 0;
  let skippedCount = 0;

  for (const sourceExpense of sourceExpenses) {
    if (sourceExpense.status === 'cancelled') {
      skippedCount += 1;
      continue;
    }

    const dueDate = shiftDateToMonth(sourceExpense.dueDate, input.targetMonth);
    const key = expenseCopyKey({ ...sourceExpense, dueDate });
    if (targetKeys.has(key)) {
      skippedCount += 1;
      continue;
    }

    await saveFinancialExpense({
      clientId: input.clientId,
      category: sourceExpense.category,
      status: sourceExpense.status === 'paid' ? 'scheduled' : sourceExpense.status,
      description: sourceExpense.description,
      amount: Number(sourceExpense.amount),
      competenceDate: `${input.targetMonth}-01`,
      dueDate,
    });
    targetKeys.add(key);
    copiedCount += 1;
  }

  return {
    sourceMonth: input.sourceMonth,
    targetMonth: input.targetMonth,
    copiedCount,
    skippedCount,
    paymentsCopied: 0,
  };
}

function expenseCopyKey(expense: Pick<FinancialExpense, 'category' | 'description' | 'amount' | 'dueDate'>): string {
  return [expense.category.trim(), expense.description.trim(), Number(expense.amount).toFixed(2), expense.dueDate ?? ''].join('|');
}

function shiftDateToMonth(date: string | null, targetMonth: string): string | null {
  if (!date) return null;
  const day = Number(date.slice(8, 10));
  const [year, month] = targetMonth.split('-').map(Number);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${targetMonth}-${String(Math.min(day, lastDay)).padStart(2, '0')}`;
}

function monthBounds(month: string): { from: string; until: string } {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) throw new Error('Informe uma competência mensal válida.');
  const year = Number(match[1]);
  const monthNumber = Number(match[2]);
  const lastDay = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return { from: `${month}-01`, until: `${month}-${String(lastDay).padStart(2, '0')}` };
}

function normalizeExpenseError(error: { code?: string; message?: string }): Error {
  if (error.code === 'PGRST202' || error.code === '42883') {
    return new Error('A integração de despesas com o caixa ainda não foi aplicada. Execute o 33a-a14-vinculos-obrigacoes-caixa.sql.');
  }
  if (error.code === '42501') return new Error('Somente administradores podem consultar ou alterar despesas.');
  if (error.code === '23505') return new Error('Esta despesa já foi paga; o segundo pagamento foi bloqueado.');
  return new Error(error.message || 'Não foi possível processar a despesa.');
}
